import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './css/CatFoodComparison.css';
import catFaceFull from '../components/img/cat-face-full.png';
import catFaceHalf from '../components/img/cat-face-half.png';
import catFaceEmpty from '../components/img/cat-face-empty.png';
import GradientButton from '../components/GradientButton';
import { API_BASE_URL } from '../config/api';

const BACKEND_URL = API_BASE_URL;

const RatingIcons = ({ rating }) => {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

  return (
    <div className="rating-icons">
      {[...Array(fullStars)].map((_, i) => (
        <img key={`full-${i}`} src={catFaceFull} alt="Full Star" className="rating-icon" />
      ))}
      {hasHalfStar && <img src={catFaceHalf} alt="Half Star" className="rating-icon" />}
      {[...Array(emptyStars)].map((_, i) => (
        <img key={`empty-${i}`} src={catFaceEmpty} alt="Empty Star" className="rating-icon" />
      ))}
      <span className="actual-rate">({rating.toFixed(1)})</span>
    </div>
  );
};

const attributeLabels = {
  brand: '品牌',
  price: '价格',
  weight: '重量',
  ingredients: '成分',
  customer_rate: '评分',
  unit_price: '单价',
};

const ComparisonModal = ({ isOpen, onClose, foods }) => {
  if (!isOpen) return null;

  const handleFoodTitleClick = (foodId) => {
    window.open(`/cat-food/${foodId}`, '_blank');
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>猫粮对比</h2>
        <table>
          <thead>
            <tr>
              <th>属性</th>
              {foods.map(food => (
                <th 
                  key={food.id} 
                  className="food-title"
                  onClick={() => handleFoodTitleClick(food.id)}
                >
                  {food.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {['brand', 'price', 'weight', 'ingredients', 'customer_rate', 'unit_price'].map(attr => (
              <tr key={attr}>
                <td>{attributeLabels[attr]}</td>
                {foods.map(food => (
                  <td key={food.id}>
                    {attr === 'customer_rate' ? (
                      <RatingIcons rating={food[attr]} />
                    ) : attr === 'unit_price' ? (
                      (food.price / (food.weight)).toFixed(2) + '(元/kg)'
                    ) : (
                      food[attr]
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <button onClick={onClose}>关闭</button>
      </div>
    </div>
  );
};

const CatFoodComparison = () => {
  const [catFoods, setCatFoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFoods, setSelectedFoods] = useState([]);
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const fetchCatFoods = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/cat-foods/`);
        setCatFoods(response.data);
        const uniqueBrands = [...new Set(response.data.map(food => food.brand))];
        setBrands(uniqueBrands);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching cat foods:', error);
        setError('Failed to fetch cat foods. Please try again later.');
        setLoading(false);
      }
    };

    fetchCatFoods();
  }, []);

  const getImageUrl = (imagePath) => {
    if (!imagePath) return '';
    return imagePath.startsWith('http') ? imagePath : `${BACKEND_URL}${imagePath}`;
  };

  const handleCheckboxChange = (e, food) => {
    e.stopPropagation();
    setSelectedFoods(prev => 
      prev.includes(food) 
        ? prev.filter(f => f.id !== food.id)
        : [...prev, food].slice(-4)
    );
  };

  const handleFoodClick = (foodId) => {
    window.open(`/cat-food/${foodId}`, '_blank');
  };

  const handleBrandClick = (brand) => {
    setSelectedBrand(selectedBrand === brand ? null : brand);
  };

  const handleCompareClick = () => {
    setIsModalOpen(true);
  };

  const filteredFoods = selectedBrand
    ? catFoods.filter(food => food.brand === selectedBrand)
    : catFoods;

  if (loading) return <div className="loading">Loading cat foods...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="cat-food-comparison-container">
      <div className="filter-by-brand">
        <h5>品牌</h5>
        {brands.map(brand => (
          <div
            key={brand}
            className={`brand-item ${selectedBrand === brand ? 'selected' : ''}`}
            onClick={() => handleBrandClick(brand)}
          >
            &#8226; {brand}
          </div>
        ))}
      </div>
      <div className="cat-food-comparison">
        <div className="food-grid">
          {filteredFoods.map((food) => (
            <div key={food.id} className="food-card">
              <input
                type="checkbox"
                className="food-checkbox"
                checked={selectedFoods.some(f => f.id === food.id)}
                onChange={(e) => handleCheckboxChange(e, food)}
              />
              <div onClick={() => handleFoodClick(food.id)}>
                {food.image && <img src={getImageUrl(food.image)} alt={food.name} className="food-image" />}
                <span className="food-name">{food.name}</span>
                <RatingIcons rating={food.customer_rate} />
                <p><strong>价格:</strong> ¥{food.price}</p>
                <p className="ingredients"><strong>成分:</strong> {food.ingredients}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="comparison-bar">
        <h5>对比栏 ({selectedFoods.length}/4)</h5>
        {selectedFoods.map(food => (
          <div key={food.id} className="compared-food">
              <img src={getImageUrl(food.image)} alt={food.name} className="compared-food-image" />
            <div className="compared-food-info">
              <h3>{food.name}</h3>
            </div>
          </div>
        ))}
        {selectedFoods.length > 0 && (
          <GradientButton className="compare-button" onClick={handleCompareClick}>
            对比
          </GradientButton>
        )}
      </div>
      <ComparisonModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        foods={selectedFoods}
      />
    </div>
  );
};

export default CatFoodComparison;
